import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { secureLog } from '@/lib/security';

interface AdminSecuritySettings {
  id: string;
  admin_id: string;
  allowed_ip_ranges?: string[];
  session_timeout_minutes: number;
  require_mfa: boolean;
  last_password_change?: string;
  is_locked: boolean;
  lock_reason?: string;
  locked_at?: string;
  locked_by?: string;
}

interface AdminAuditLog {
  id: string;
  admin_id: string;
  action_type: string;
  resource_type?: string;
  resource_id?: string;
  details?: any;
  ip_address?: unknown;
  user_agent?: string;
  created_at: string;
}

export const useAdminSecurity = () => {
  const [settings, setSettings] = useState<AdminSecuritySettings | null>(null);
  const [auditLogs, setAuditLogs] = useState<AdminAuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Load admin security settings
  const loadSettings = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('admin_security_settings')
        .select('*')
        .eq('admin_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        secureLog.error('Error loading admin security settings', error);
        return;
      }

      setSettings(data);
    } catch (error) {
      secureLog.error('Error in loadSettings', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load audit logs
  const loadAuditLogs = useCallback(async (limit = 50) => {
    try {
      const { data, error } = await supabase
        .from('admin_audit_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        secureLog.error('Error loading audit logs', error);
        return;
      }

      setAuditLogs(data || []);
    } catch (error) {
      secureLog.error('Error in loadAuditLogs', error);
    }
  }, []);

  // Log admin action
  const logAdminAction = useCallback(async (
    actionType: string,
    resourceType?: string,
    resourceId?: string,
    details?: any
  ) => {
    try {
      const { error } = await supabase.rpc('log_admin_action', {
        _action_type: actionType,
        _resource_type: resourceType,
        _resource_id: resourceId,
        _details: details
      });

      if (error) {
        secureLog.error('Error logging admin action', error);
      }
    } catch (error) {
      secureLog.error('Error in logAdminAction', error);
    }
  }, []);

  // Update IP restrictions
  const updateIPRestrictions = useCallback(async (ipRanges: string[]) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('admin_security_settings')
        .upsert({
          admin_id: user.id,
          allowed_ip_ranges: ipRanges
        });

      if (error) {
        secureLog.error('Error updating IP restrictions', error);
        toast({
          title: "Error",
          description: "Failed to update IP restrictions",
          variant: "destructive"
        });
        return;
      }

      await logAdminAction('update_ip_restrictions', 'admin_security_settings', user.id, { ip_ranges: ipRanges });
      await loadSettings();
      
      toast({
        title: "Success",
        description: "IP restrictions updated successfully"
      });
    } catch (error) {
      secureLog.error('Error in updateIPRestrictions', error);
    }
  }, [logAdminAction, loadSettings, toast]);

  // Lock/unlock admin
  const toggleAdminLock = useCallback(async (adminId: string, isLocked: boolean, reason?: string) => {
    try {
      const { error } = await supabase
        .from('admin_security_settings')
        .upsert({
          admin_id: adminId,
          is_locked: isLocked,
          lock_reason: reason,
          locked_at: isLocked ? new Date().toISOString() : null,
          locked_by: isLocked ? (await supabase.auth.getUser()).data.user?.id : null
        });

      if (error) {
        secureLog.error('Error toggling admin lock', error);
        toast({
          title: "Error",
          description: `Failed to ${isLocked ? 'lock' : 'unlock'} admin`,
          variant: "destructive"
        });
        return;
      }

      await logAdminAction(
        isLocked ? 'lock_admin' : 'unlock_admin',
        'admin_security_settings',
        adminId,
        { reason }
      );
      
      toast({
        title: "Success",
        description: `Admin ${isLocked ? 'locked' : 'unlocked'} successfully`
      });
    } catch (error) {
      secureLog.error('Error in toggleAdminLock', error);
    }
  }, [logAdminAction, toast]);

  // Update session timeout
  const updateSessionTimeout = useCallback(async (timeoutMinutes: number) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('admin_security_settings')
        .upsert({
          admin_id: user.id,
          session_timeout_minutes: timeoutMinutes
        });

      if (error) {
        secureLog.error('Error updating session timeout', error);
        toast({
          title: "Error",
          description: "Failed to update session timeout",
          variant: "destructive"
        });
        return;
      }

      await logAdminAction('update_session_timeout', 'admin_security_settings', user.id, { timeout_minutes: timeoutMinutes });
      await loadSettings();
      
      toast({
        title: "Success",
        description: "Session timeout updated successfully"
      });
    } catch (error) {
      secureLog.error('Error in updateSessionTimeout', error);
    }
  }, [logAdminAction, loadSettings, toast]);

  useEffect(() => {
    loadSettings();
    loadAuditLogs();
  }, [loadSettings, loadAuditLogs]);

  return {
    settings,
    auditLogs,
    loading,
    logAdminAction,
    updateIPRestrictions,
    toggleAdminLock,
    updateSessionTimeout,
    refetchSettings: loadSettings,
    refetchAuditLogs: loadAuditLogs
  };
};